import { useState, useEffect } from 'react';
import { Modal, ModalFooter } from './Modal';
import { Input, Select } from './FormField';
import { Button } from '../ui/Button';
import { ErrorDisplay } from '../ui/ErrorDisplay';
import { subProductApi, productApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface SubProduct {
  id: string;
  name: string;
  productId: string;
  displayOrder: number;
  _count?: {
    initiatives: number;
  };
}

interface Product {
  id: string;
  name: string;
}

interface SubProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  subProduct?: SubProduct | null;
  onSuccess: () => void;
  preselectedProductId?: string;
}

export function SubProductModal({
  isOpen,
  onClose,
  subProduct,
  onSuccess,
  preselectedProductId,
}: SubProductModalProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    productId: preselectedProductId || '',
    displayOrder: 0,
  });
  const [products, setProducts] = useState<Product[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const isEditMode = Boolean(subProduct);

  // Load products on mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoading(true);
        const data = await productApi.getAll();
        setProducts(data);
      } catch (error) {
        console.error('Failed to load products:', error);
        setErrors({
          submit: 'Failed to load products',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      loadProducts();
    }
  }, [isOpen]);

  // Populate form when editing
  useEffect(() => {
    if (subProduct) {
      setFormData({
        name: subProduct.name || '',
        productId: subProduct.productId || '',
        displayOrder: subProduct.displayOrder || 0,
      });
    } else {
      setFormData({
        name: '',
        productId: preselectedProductId || '',
        displayOrder: 0,
      });
    }
    setErrors({});
    setSubmitError(null);
  }, [subProduct, preselectedProductId, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (!formData.productId) {
      newErrors.productId = 'Product is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const data = {
        name: formData.name.trim(),
        productId: formData.productId,
        displayOrder: formData.displayOrder,
        createdBy: user?.id,
      };

      if (isEditMode && subProduct) {
        await subProductApi.update(subProduct.id, data);
      } else {
        await subProductApi.create(data);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save sub-product:', error);
      setSubmitError(error instanceof Error ? error : new Error('Failed to save sub-product'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!subProduct) return;

    if (!confirm(`Are you sure you want to delete "${subProduct.name}"?`)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await subProductApi.delete(subProduct.id);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to delete sub-product:', error);
      setSubmitError(error instanceof Error ? error : new Error('Failed to delete sub-product'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const productOptions = products.map((product) => ({
    value: product.id,
    label: product.name,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit sub-product' : 'Add new sub-product'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {isLoading ? (
          <div className="py-8 text-center text-text-secondary">
            Loading products...
          </div>
        ) : (
          <>
            <Select
              label="Product"
              name="productId"
              value={formData.productId}
              onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
              options={productOptions}
              placeholder="Select a product"
              required
              error={errors.productId}
              helpText="Select the parent product"
            />

            <Input
              label="Name"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Debit card"
              required
              error={errors.name}
            />

            <Input
              label="Display order"
              name="displayOrder"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              min={0}
              helpText="Controls the order in which sub-products appear in lists"
            />

            {isEditMode && subProduct?._count && (
              <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
                <p className="text-sm text-blue-800">
                  This sub-product has <strong>{subProduct._count.initiatives}</strong> initiative
                  {subProduct._count.initiatives !== 1 ? 's' : ''}
                </p>
              </div>
            )}

            <ErrorDisplay error={submitError} />

            <ModalFooter>
              {isEditMode && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                  className="mr-auto"
                >
                  Delete
                </Button>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : isEditMode ? 'Save changes' : 'Add sub-product'}
              </Button>
            </ModalFooter>
          </>
        )}
      </form>
    </Modal>
  );
}
